import { apiUser } from "@/components/toLoad/api";
import { setSnackbar } from "@/stores/ui";

export const QUOTA_EXCEEDED_MESSAGE = "Quota exceeded";
export const IA_MODULE_ID = "xKtnIntelligenceMod1";
export const MONTHLY_QUOTA_COLLECTIONS = new Set(["quotes", "invoices"]);

export function isIntelligenceModule(module = {}) {
  if (module?.id === IA_MODULE_ID) return true;
  return String(module?.slug || "").toLowerCase() === "intelligence";
}

const CHARS_PER_TOKEN = 4;
const TEXT_TOTAL_OVER_PROMPT = 2.2;
const IMAGE_COMPLETION_RESERVE = 2200;

function parseCreationDate(raw) {
  if (!raw) return null;
  let date = null;
  if (typeof raw.toDate === "function") date = raw.toDate();
  else if (typeof raw.seconds === "number") date = new Date(raw.seconds * 1000);
  else date = new Date(raw);
  if (!date || Number.isNaN(date.getTime())) return null;
  return date;
}

export function resolveQuota(raw) {
  if (raw == null || raw === "") return -1;
  if (raw === -1 || raw === "-1") return -1;
  const n = Number(raw);
  if (!Number.isFinite(n)) return -1;
  return n;
}

export function currentPeriodKey(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Paris",
    year: "numeric",
    month: "2-digit",
  }).formatToParts(date);
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  return `${year}-${month}`;
}

export function monthlyAiTokens(module, date = new Date()) {
  if (!module) return 0;
  const period = currentPeriodKey(date);
  if (String(module.tokensPeriod || "") !== period) return 0;
  return Number(module.tokens || 0);
}

export function lifetimeAiTokens(module, monthlyBreakdown = []) {
  const stored = Number(module?.tokensTotal || 0);
  const fromLogs = (monthlyBreakdown || []).reduce((sum, row) => sum + Number(row.tokens || 0), 0);
  return Math.max(stored, fromLogs);
}

export function aggregateAiUsageByMonth(docs = []) {
  const byMonth = new Map();

  for (const doc of docs) {
    const date = parseCreationDate(doc.creationDate);
    if (!date) continue;

    const key = currentPeriodKey(date);
    byMonth.set(key, (byMonth.get(key) || 0) + Number(doc.tokens || 0));
  }

  return [...byMonth.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([period, tokens]) => ({ period, tokens }));
}

export function resolveCollectionAlias(collection) {
  if (collection === "logistics") return "warehouses";
  return collection;
}

export function moduleCollections(collection) {
  return String(collection ?? "")
    .split(",")
    .map((item) => resolveCollectionAlias(item.trim()))
    .filter(Boolean);
}

export function isMonthlyQuotaCollection(collection) {
  return MONTHLY_QUOTA_COLLECTIONS.has(resolveCollectionAlias(collection));
}

export function isMonthlyQuotaModule({ slug, collection } = {}) {
  if (String(slug || "").toLowerCase() === "intelligence") return true;
  return isMonthlyQuotaCollection(collection);
}

export function countInCurrentPeriod(docs = [], date = new Date()) {
  const period = currentPeriodKey(date);
  let count = 0;
  for (const doc of docs) {
    const created = parseCreationDate(doc.creationDate);
    if (!created) continue;
    if (currentPeriodKey(created) === period) count += 1;
  }
  return count;
}

export function estimateTextTokens(parts = [], maxCompletionTokens = -1) {
  const chars = parts.reduce((sum, part) => sum + String(part || "").length, 0);
  const promptEst = Math.max(1, Math.ceil(chars / CHARS_PER_TOKEN));
  if (maxCompletionTokens != null && maxCompletionTokens > 0) {
    return promptEst + Math.ceil(maxCompletionTokens);
  }
  return Math.max(1, Math.ceil(promptEst * TEXT_TOTAL_OVER_PROMPT));
}

export function estimateImageTokens(prompt) {
  const promptEst = Math.max(1, Math.ceil(String(prompt || "").length / CHARS_PER_TOKEN));
  return promptEst + IMAGE_COMPLETION_RESERVE;
}

export function isQuotaExceededError(error) {
  const message = error?.response?.data?.message || error?.message || "";
  return error?.response?.status === 403 && String(message).includes(QUOTA_EXCEEDED_MESSAGE);
}

export function showQuotaExceededSnackbar() {
  setSnackbar({
    text: "Votre quota pour ce module est atteint.",
    color: "error",
  });
}

export async function assertCanCreate(companyId, collection, increment = 1) {
  if (!companyId || !collection) return true;

  const resolved = resolveCollectionAlias(collection);

  const [enabledRes, catalogRes] = await Promise.all([
    apiUser({ url: `/companies/${companyId}/modules` }),
    apiUser({ url: "/admin/modules" }),
  ]);

  const catalogById = new Map(
    (catalogRes.data.documents || []).map((module) => [module.id, module]),
  );

  let matched = null;
  let matchedCollections = [];
  for (const module of enabledRes.data.documents || []) {
    const catalogModule = catalogById.get(module.id);
    const collections = moduleCollections(catalogModule?.collection ?? module.collection);
    if (collections.includes(resolved)) {
      matched = module;
      matchedCollections = collections;
      break;
    }
  }

  if (!matched) return true;

  const quota = resolveQuota(matched.quota);
  if (quota === -1) return true;

  const lists = await Promise.all(
    matchedCollections.map((name) => apiUser({ url: `/companies/${companyId}/${name}` })),
  );
  const size = lists.reduce((sum, listRes, index) => {
    const docs = listRes.data.documents || [];
    return sum + (isMonthlyQuotaCollection(matchedCollections[index])
      ? countInCurrentPeriod(docs)
      : (listRes.data.size ?? docs.length));
  }, 0);
  if (size + increment > quota) {
    showQuotaExceededSnackbar();
    return false;
  }

  return true;
}
